import { a3Copy } from "@/components/assignments/a3-copy";
import { useCallback, useEffect, useRef, useState } from "react";
import { assignmentsApi, type ArtifactUploadPolicy, type ReferenceDocument, type ReferenceDraft, type ReferenceSave } from "@/lib/api/assignments";
import { ApiError } from "@/lib/api/client";
import { reasonOnError } from "@/lib/api/commitSeams";

const same = (a: ReferenceDocument | null, b: ReferenceDocument | null) => a?.type === b?.type && (a?.type === "link" && b?.type === "link" ? a.url === b.url && (a.title ?? "") === (b.title ?? "") : a?.type === "artifact" && b?.type === "artifact" ? a.reservation_id === b.reservation_id : a === b);
type Save = { base: ReferenceDraft; body: ReferenceSave };

/** The server's rule for a saved link, so a refused scheme is a field error and never a save. */
export function savableLink(url: string): boolean {
  if (!url) return true;
  if (url.length > 2048 || url !== url.trim() || !/^https?:\/\//.test(url)) return false;
  try { new URL(url); return true; } catch { return false; }
}
const savable = (document: ReferenceDocument) => document.type !== "link" || savableLink(document.url);
const emptyReference = (kind: "link" | "artifact"): ReferenceDocument => kind === "link" ? { type: "link", url: "" } : { type: "artifact", reservation_id: null };

/** Revisioned private references use field saves. A hand-in waits for the acknowledged exact document. */
export function useReferenceDraft(assignmentId: string, kind: "link" | "artifact", cycle: number, allowed: boolean, initialDocument?: ReferenceDocument, expectedParentSubmissionId?: string | null) {
  const [draft, setDraft] = useState<ReferenceDraft | null>(null);
  const [document, setDocument] = useState<ReferenceDocument | null>(null);
  const [error, setError] = useState<string | null>(null), [loading, setLoading] = useState(true), [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<ReferenceDraft | null>(null);
  const [unknownSave, setUnknownSave] = useState(false);
  const [readFailed, setReadFailed] = useState(false);
  const [uploadPolicy, setUploadPolicy] = useState<ArtifactUploadPolicy | null>(null);
  const base = useRef<ReferenceDraft | null>(null), current = useRef<ReferenceDocument | null>(null);
  const parent = useRef(expectedParentSubmissionId);
  const belongs = (value: ReferenceDraft) => parent.current === undefined ? value.cycle === cycle : value.parent_submission_id === parent.current;
  const initial = useRef(initialDocument ?? emptyReference(kind));
  const operation = useRef<Save | null>(null), flight = useRef<Promise<ReferenceDraft> | null>(null);
  const permitted = useRef(allowed); permitted.current = allowed;
  const live = useRef(true);
  const adopt = useCallback((value: ReferenceDraft, fill = false) => {
    base.current = value;
    if (live.current) setDraft(value);
    if (fill || current.current === null) { current.current = value.document; if (live.current) setDocument(value.document); }
  }, []);
  const load = useCallback(async () => {
    setLoading(true); setError(null); setReadFailed(false);
    try {
      const read = await assignmentsApi.readReference(assignmentId);
      if (!live.current) return;
      setUploadPolicy(read.upload_policy ?? null);
      const value = read.draft;
      if (!value || !(parent.current === undefined ? value.cycle === cycle : value.parent_submission_id === parent.current) || value.submitted_id) {
        if (base.current) throw new Error(a3Copy.yourPrivateReferenceIsUnavailableForThisAttemptYour);
        if (current.current === null) { current.current = initial.current; setDocument(current.current); }
        return;
      }
      if (value.document.type !== kind) throw new Error(a3Copy.yourPrivateReferenceIsUnavailableForThisAttemptYour);
      if (value.erased_at) throw new Error(a3Copy.thisPrivateReferenceWasErasedYourEntryStaysHere);
      const dirty = current.current !== null && base.current && !same(current.current, base.current.document);
      if (dirty && value.revision !== base.current?.revision) setConflict(value);
      else adopt(value, !dirty);
    } catch (cause) { if (live.current) { setReadFailed(true); setError(reasonOnError(cause)?.message ?? (cause instanceof Error ? cause.message : a3Copy.yourPrivateReferenceCouldNotBeRead)); } }
    finally { if (live.current) setLoading(false); }
  }, [assignmentId, kind, cycle, adopt]);
  useEffect(() => { live.current = true; void load(); return () => { live.current = false; }; }, [load]);
  const change = (value: ReferenceDocument) => { current.current = value; setDocument(value); if (!operation.current) setError(null); };
  const acknowledge = (sent: Save, answer: Pick<ReferenceDraft, "revision" | "saved_at" | "content_digest">) => {
    const value = { ...sent.base, ...answer, document: sent.body.document };
    adopt(value); operation.current = null; if (live.current) { setUnknownSave(false); setError(null); }
    return value;
  };
  async function checkSaved(): Promise<ReferenceDraft | null> {
    const sent = operation.current;
    if (!sent) return base.current;
    const answer = await assignmentsApi.confirmReferenceSave(assignmentId, sent.base.id, sent.body);
    if (!answer.acknowledged) { setError(a3Copy.thatSaveWasNotRecordedYourEntryIsKept); return null; }
    if (answer.revision == null || !answer.saved_at || !answer.content_digest) throw new Error(a3Copy.theSaveAcknowledgmentIsIncompleteKeepYourEntryAnd);
    return acknowledge(sent, { revision: answer.revision, saved_at: answer.saved_at, content_digest: answer.content_digest });
  }
  async function persist(value?: ReferenceDocument): Promise<ReferenceDraft> {
    if (flight.current) { await flight.current; return persist(value); }
    if (conflict) throw new Error(a3Copy.compareTheSavedReferenceWithYourEntryBeforeSaving);
    if (readFailed) throw new Error(a3Copy.readYourPrivateDraftAgainBeforeSavingYourEntry);
    if (!permitted.current) throw new Error(a3Copy.thisWorkIsClosedToNewHandInsYour2);
    const unsent = value ?? current.current;
    // The field states the refusal; nothing is sent, so no save error stacks beside it.
    if (unsent && !savable(unsent)) throw new Error(a3Copy.yourLinkMustBeACompleteHttpOrHttps);
    const job = async () => {
      let sent = operation.current;
      if (sent) {
        const checked = await checkSaved();
        if (!checked) acknowledge(sent, await assignmentsApi.saveReference(assignmentId, sent.base.id, sent.body));
      }
      const target = value ?? current.current;
      let at = base.current;
      if (!at && target && !same(target, emptyReference(kind))) {
        at = await assignmentsApi.startReference(assignmentId, kind);
        if (!belongs(at) || at.document.type !== kind) throw new Error(a3Copy.yourPrivateReferenceIsUnavailableForThisAttemptYour);
        adopt(at);
      }
      if (!at || !target || at.erased_at || at.submitted_id) throw new Error(a3Copy.readYourCurrentPrivateDraftBeforeHandingIn);
      if (same(target, at.document)) return at;
      sent = { base: at, body: { operation_id: crypto.randomUUID(), expected_revision: at.revision, document: target } };
      operation.current = sent;
      return acknowledge(sent, await assignmentsApi.saveReference(assignmentId, at.id, sent.body));
    };
    setSaving(true);
    const running = job(); flight.current = running;
    try { return await running; }
    catch (cause) {
      const definite = cause instanceof ApiError && cause.status >= 400 && cause.status < 500 && cause.status !== 408;
      if (definite) {
        operation.current = null; setUnknownSave(false);
        if (cause.status === 409) {
          try { const newer = (await assignmentsApi.readReference(assignmentId)).draft; if (newer && !newer.erased_at && !newer.submitted_id) setConflict(newer); } catch { /* The local entry remains. */ }
        }
      } else if (operation.current) setUnknownSave(true);
      setError(reasonOnError(cause)?.message ?? (definite ? cause.message : operation.current ? a3Copy.saveNotConfirmedYourEntryStaysHereCheckThis : cause instanceof Error ? cause.message : a3Copy.yourReferenceWasNotSaved));
      throw cause;
    } finally { flight.current = null; if (live.current) setSaving(false); }
  }
  // An empty entry with no started draft has nothing to save, even when it clears a prefilled link.
  const nothingToSave = draft === null && document !== null && same(document, emptyReference(kind));
  // A private URL survives ordinary editing and device changes; typing never hands it in.
  useEffect(() => {
    if (!allowed || !document || document.type !== "link" || !savable(document) || same(document, draft?.document ?? initial.current) || nothingToSave || conflict || unknownSave || readFailed) return;
    const timer = setTimeout(() => { void persist().catch(() => undefined); }, 600);
    return () => clearTimeout(timer);
  });
  return { draft, document, current, loading, saving, error, readFailed, conflict, unknownSave, uploadPolicy, unsaved: document !== null && !nothingToSave && !same(document, draft?.document ?? initial.current), change, persist, load,
    checkSaved: async () => { try { return await checkSaved(); } catch (cause) { setError(cause instanceof Error ? cause.message : a3Copy.theSaveCouldNotBeChecked); return null; } },
    keepMine: () => { if (conflict) adopt(conflict); setConflict(null); operation.current = null; setUnknownSave(false); setError(null); },
    useRecorded: () => { if (conflict) adopt(conflict, true); setConflict(null); operation.current = null; setUnknownSave(false); setError(null); },
  };
}
